// ═══════════════════════════════════════════════════════════════════════════
// @dxf-ai-editor/ai-commands — canonical JSON (RFC-8785 JCS, [N1] / §3.2 [L7])
// ═══════════════════════════════════════════════════════════════════════════
//
// The ONE RFC-8785 (JCS) canonicalization algorithm the agent-bridge surfaces
// need (keys sorted by UTF-16 code unit, no insignificant whitespace, canonical
// numbers, `-0 ≡ 0`, arrays/objects recursed). Lives in `ai-commands` — the LEAF
// package BOTH `apps/node-companion` AND `apps/web` already depend on — so the two
// consumers share ONE truth instead of each maintaining a copy:
//
//   • the BROWSER `command.run` batchHash (ABB-S4 `apps/web/.../agentBridge/
//     batchHash.ts`) — the browser-authoritative replay identity (INV-HASH);
//   • the COMPANION batchHash ([N1] `apps/node-companion/.../correlation.ts`) +
//     the projection-cursor envelope ([L7] `cursorKeyEnvelope.ts`), both via
//     `apps/node-companion/.../canonicalJson.ts` which now DELEGATES here.
//
// A cross-APP import (apps/web → apps/node-companion) is forbidden (ПРИОРИТЕТ #2),
// so the algorithm cannot live in node-companion and be imported by web. Hoisting
// it to this shared leaf is the one-truth fix that keeps the two sides byte-for-
// byte identical (a drift would split the replay identity → a phantom non-replay).
//
// The call sites differ in TWO documented POLICY knobs (NOT two algorithms):
//   • `onNonFinite` — NaN/Infinity → throw (batchHash) OR `"null"` (cursor);
//   • `onNonJson`   — undefined/function/symbol/bigint → throw OR `"null"`;
//   • `nfcStrings`  — NFC-normalize strings + keys ([N1.e]) — batchHash yes, cursor no.
// Both are RFC-8785-grounded variants of the SAME traversal — one impl, explicit
// knobs, NOT a behaviour flag hiding two functions.
//
// LEAF module: imports NOTHING (pure string traversal). No http/ws/crypto, no
// editorStore, no node-companion.
// ═══════════════════════════════════════════════════════════════════════════

/** The two policy knobs the call sites differ on (see the module header). */
export type CanonicalJsonOptions = {
  /**
   * A non-finite number (NaN/Infinity) handler: return the canonical token (`"null"`)
   * OR THROW (the batchHash path rejects). Receives the offending value.
   */
  onNonFinite: (value: number) => string;
  /**
   * A non-JSON-representable value handler (undefined/function/symbol/bigint): return
   * the canonical token (`"null"`) OR THROW. Receives the `typeof` string.
   */
  onNonJson: (typeofValue: string) => string;
  /** Whether to NFC-normalize strings + object keys ([N1.e]). */
  nfcStrings: boolean;
};

/**
 * Canonicalize a JSON-representable value to its RFC-8785 (JCS) string form, applying
 * the call site's `options` policy for non-finite / non-JSON / NFC. Deterministic +
 * total over JSON values: the SAME logical value always yields the SAME string.
 */
export function canonicalizeJson(value: unknown, options: CanonicalJsonOptions): string {
  if (value === null) {
    return "null";
  }
  const t = typeof value;
  if (t === "boolean") {
    return value ? "true" : "false";
  }
  if (t === "number") {
    const n = value as number;
    if (!Number.isFinite(n)) {
      return options.onNonFinite(n);
    }
    // Canonical number: `JSON.stringify` of a finite number yields the shortest
    // round-trip form (integers without a trailing ".0"/exponent); normalize `-0`→`0`.
    return Object.is(n, -0) ? "0" : JSON.stringify(n);
  }
  if (t === "string") {
    const s = options.nfcStrings ? (value as string).normalize("NFC") : (value as string);
    return JSON.stringify(s);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalizeJson(item, options)).join(",")}]`;
  }
  if (t === "object") {
    const obj = value as Record<string, unknown>;
    // Sort keys by UTF-16 code unit (the default lexicographic order); skip
    // `undefined`-valued keys (JSON.stringify omits them — not JSON-representable).
    const keys = Object.keys(obj)
      .filter((k) => obj[k] !== undefined)
      .sort();
    const members = keys.map((k) => {
      const key = options.nfcStrings ? k.normalize("NFC") : k;
      return `${JSON.stringify(key)}:${canonicalizeJson(obj[k], options)}`;
    });
    return `{${members.join(",")}}`;
  }
  // `undefined` / function / symbol / bigint are not JSON-representable.
  return options.onNonJson(t);
}
